/**
 * @file stock-level-columns.tsx
 * @description Колонки таблицы остатков — реестром (phase19.31, SC59).
 *
 * Состав по макету SC59: «Товар / артикул», «Склад» (только когда складов
 * несколько и склад не выбран), «Партия», «Годен до», «Ячейка», «Кол-во»,
 * «Резерв», «Доступно». В отборе по этикеткам «Резерв» и «Доступно»
 * уступают место колонкам «Этикетки» и «Печать» (phase19.22, W17-9c).
 *
 * На 1024–1279 колонка содержимого ~700px: «Годен до» уходит второй линией
 * под «Партию», «Склад» — второй линией к артикулу, «Резерв» — под
 * количество (если он есть). С 1280 — все столбцы. Колонка «Товар» гибкая:
 * название целиком первой линией, артикул — второй (приёмка qa4 п. 5).
 */

import type { ReactNode } from "react";

import { StockBinCell, StockExpiryCell, StockLabelQtyInput, StockLotCell } from "./StockLevelCells";
import { formatStockQty, stockAvailable, stockExpiryView, type StockRowViewProps } from "./stock-level-format";
import { LabelStatusBadge } from "../labels/LabelStatusBadge";
import { formatSkuName, type StockLevelDto } from "../../../lib/api-wms";
import { cn } from "@/lib/utils";

/** Столбец таблицы остатков. */
export interface StockColumn {
  key: string;
  header: string;
  /** Класс ширины для `<col>`; нет — забирает остаток. */
  width?: string;
  align?: "end";
  cell: (level: StockLevelDto) => ReactNode;
}

type ColumnsInput = Pick<StockRowViewProps, "labelsMode" | "isSelected" | "qtyOf" | "onQty" | "showWarehouse"> & {
  /** С 1280 — все столбцы; уже — второстепенное второй линией. */
  wide: boolean;
};

/** Вторая линия ячейки — подпись вторым цветом. */
function withCaption(main: ReactNode, caption: ReactNode): ReactNode {
  if (caption === null) return main;
  return (
    <span className="flex min-w-0 flex-col">
      <span className="min-w-0">{main}</span>
      <span className="type-caption truncate text-on-surface-variant">{caption}</span>
    </span>
  );
}

/**
 * Название — первой линией во всю ширину колонки, артикул — второй (как в
 * реестре заказов). Артикул в одной строке с названием отнимал у него место:
 * «Алтайская маг…» при свободном месте справа (приёмка qa4 п. 5). Склад на
 * 1024–1279 — в той же второй линии через « · ».
 */
function productCell(level: StockLevelDto, warehouseLine: boolean): ReactNode {
  const name = formatSkuName(level.sku);
  return (
    <span className="flex min-w-0 flex-col">
      <span className="min-w-0 truncate font-medium" title={name}>
        {name}
      </span>
      <span className="type-caption min-w-0 truncate text-on-surface-variant">
        <span className="font-mono text-xs">{level.sku?.code ?? "—"}</span>
        {warehouseLine ? ` · ${level.warehouse.name}` : null}
      </span>
    </span>
  );
}

export function stockTableColumns({ labelsMode, isSelected, qtyOf, onQty, showWarehouse, wide }: ColumnsInput): StockColumn[] {
  const reservedCaption = (level: StockLevelDto) =>
    Number(level.totalReserved) > 0 ? `резерв ${formatStockQty(level.totalReserved)}` : null;

  const columns: StockColumn[] = [
    { key: "product", header: "Товар / артикул", cell: (level) => productCell(level, showWarehouse && !wide) },
  ];
  if (showWarehouse && wide) {
    columns.push({ key: "warehouse", header: "Склад", width: "w-[120px]", cell: (level) => <span className="block truncate">{level.warehouse.name}</span> });
  }
  columns.push({
    key: "lot",
    header: wide ? "Партия" : "Партия · годен до",
    width: wide ? "w-[136px]" : "w-[144px]",
    cell: (level) => {
      const expiry = stockExpiryView(level);
      if (wide) return <StockLotCell level={level} />;
      return withCaption(
        <StockLotCell level={level} />,
        expiry.short ? <span className={expiry.tone === "warning" ? "text-warning" : undefined}>{expiry.short}</span> : null,
      );
    },
  });
  if (wide) columns.push({ key: "expiry", header: "Годен до", width: "w-[108px]", cell: (level) => <StockExpiryCell level={level} /> });
  columns.push(
    { key: "bin", header: "Ячейка", width: "w-[100px]", cell: (level) => <StockBinCell level={level} /> },
    {
      key: "quantity",
      header: "Кол-во",
      width: "w-[80px]",
      align: "end",
      cell: (level) => {
        const qty = <span className="tabular-nums">{formatStockQty(level.quantity)}</span>;
        return wide || labelsMode ? qty : withCaption(qty, reservedCaption(level));
      },
    },
  );

  if (labelsMode) {
    columns.push(
      {
        key: "labels",
        header: "Этикетки",
        width: "w-[112px]",
        cell: (level) => <LabelStatusBadge status={level.lot?.labelStatus} printedAt={level.lot?.labelsPrintedAt} variant="cell" />,
      },
      {
        key: "print",
        header: "Печать",
        width: "w-[88px]",
        align: "end",
        cell: (level) =>
          isSelected(level) && qtyOf && onQty ? (
            <StockLabelQtyInput level={level} value={qtyOf(level)} onChange={(qty) => onQty(level, qty)} />
          ) : (
            <span className="text-on-surface-variant">—</span>
          ),
      },
    );
    return columns;
  }

  if (wide) {
    columns.push({
      key: "reserved",
      header: "Резерв",
      width: "w-[80px]",
      align: "end",
      cell: (level) => <span className="tabular-nums text-on-surface-variant">{formatStockQty(level.totalReserved)}</span>,
    });
  }
  columns.push({
    key: "available",
    header: "Доступно",
    width: "w-[92px]",
    align: "end",
    cell: (level) => {
      const available = stockAvailable(level);
      return <span className={cn("font-medium tabular-nums", available < 0 && "text-[var(--destructive-text)]")}>{formatStockQty(available)}</span>;
    },
  });
  return columns;
}
